/* eslint-disable jsx-a11y/media-has-caption */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { makeGet, makePatch, makePost } from "@culturehq/client";
import { ActionButton, BooleanField, classnames, DateTimeField, Icon, ModalDialog, PlainButton, SelectField, Thumbnail, Tooltip } from "@culturehq/components";

import linkedin from "images/social/linkedin_white.svg";

import { useProfile } from "app/context";
import { ProfileLink } from "app/links";
import { errorService, getImageFromStory, t, Trans } from "app/utils";

import { getDefaultStartsAt } from "constants/eventTypes";
import imagesSizes from "constants/imagesSizes";

import LinkedinImageSlider from "components/common/gallery/LinkedinImageSlider";
import Subtitles from "components/common/Subtitles";

import ErrorMessage from "components/common/ErrorMessage";
import general from "constants/general";
import LazyEditor from "components/editor/LazyEditor";
import formatLinkedinMention from "components/editor/formatLinkedinMention";
import ChqModalDialog from "components/common/ChqModalDialog";
import getUserName from "app/utils/getUserName";
import LinkedinPostCreator from "./LinkedinPostCreator";
import LinkedinMentionText from "./LinkedinMentionText";

export const linkedinVisibilityOptions = [
  { label: "Anyone", value: "PUBLIC" },
  { label: "Connections", value: "CONNECTIONS" }
];

const ChildrenWrapper = ({ children, disabled, onOpen }) => (
  <div onClick={() => { if (!disabled) onOpen(); }} role="button" tabIndex={0}>
    {children}
  </div>
);

const WithoutChildren = ({ disabled, onOpen }) => (
  <div className="gallery-lightbox__header gallery-lightbox__header--Linkedin" role="button" onClick={!disabled && onOpen} tabIndex={0}>
    <span className="legend">
      <Icon icon="share" />
      {" "}
      <Trans>Click here to share this post on LinkedIn</Trans>
    </span>
  </div>
);

const ButtonWrapper = ({ children, disabled, onOpen }) => (
  children ? (
    <ChildrenWrapper disabled={disabled} onOpen={onOpen}>
      {children}
    </ChildrenWrapper>
  ) : <WithoutChildren disabled={disabled} onOpen={onOpen} />
);

const StoryShareLinkedin = ({
  approvedForSocial,
  approvedForSocialMessage,
  children,
  linkedinShare,
  onShared,
  onCreate,
  onEdit,
  placeholder,
  story
}) => {
  const mediaRef = React.createRef();
  const editorRef = useRef(null);
  const { profile } = useProfile();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showVideoInfo, setShowVideoInfo] = useState(false);
  const [linkedinOrganizations, setLinkedinOrganizations] = useState([
    { label: getUserName(profile), value: profile?.userLinkedin?.linkedinId }
  ]);
  const [linkedinAuthor, setLinkedinAuthor] = useState(profile?.userLinkedin?.linkedinId);
  const [shareMessage, setShareMessage] = useState();
  const [orgsInfo, setOrgsInfo] = useState({});
  const [visibility, setVisibility] = useState("PUBLIC");
  const [error, setError] = useState();
  const [schedulePost, setSchedulePost] = useState(false);
  const [timezoneOffset] = useState(-new Date().getTimezoneOffset());
  const [scheduleAt, setScheduleAt] = useState(getDefaultStartsAt());
  const [disabled, setDisabled] = useState(true);
  const WIDTH_THRESHOLD = 290;
  const HEIGHT_THRESHOLD = 290;

  useEffect(() => {
    const processImages = async () => {
      const promises = story.visuals.map(visual => new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = err => {
          errorService.error(`The image could not be loaded - ${profile.id} - ${story.id}`);
          // eslint-disable-next-line no-console
          console.error("The image could not be loaded");
          resolve(null);
        };
        img.src = visual.url;
      }));

      const processedImages = await Promise.all(promises);
      const disabled = !!processedImages.find(
        img => !img || img.width < WIDTH_THRESHOLD || img.height < HEIGHT_THRESHOLD
      );
      setDisabled(disabled);
    };

    processImages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story.visuals]);

  useEffect(
    () => {
      if (!placeholder) {
        const paths = location.pathname.split("/");
        if (paths[paths.length - 1] === "share" && `${story.id}` === paths[paths.length - 2]) {
          onOpen();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, profile, story.id, linkedinShare, placeholder]
  );

  useEffect(
    () => {
      if (shareMessage?.length > general.LINKEDIN_CHARS_RESTRICTION) {
        setError(t("Max number of characters not allowed"));
      } else {
        setError();
      }
    }, [shareMessage]
  );

  useEffect(
    () => {
      const fetcOrgs = async () => {
        const { organizations } = await makeGet("/linkedin/organizations");
        const orgsIndex = {};
        const results = [{ label: getUserName(profile), value: profile?.userLinkedin?.linkedinId }];
        organizations.forEach(organization => {
          results.push({ label: organization.localizedName, value: organization.$URN });
          orgsIndex[organization.$URN] = organization;
        });
        setOrgsInfo(orgsIndex);
        setLinkedinOrganizations(results);
      };

      if (open) {
        fetcOrgs();
      }
    },
    [open, profile]
  );

  useEffect(
    () => {
      const approvedForSocialMessage = approvedForSocial?.approvedForSocialMessages?.find(approvedMessage => approvedMessage.socialType === "linkedin");
      if (approvedForSocialMessage?.message?.length > 0 && !approvedForSocialMessage) {
        setShareMessage(approvedForSocialMessage.message);
      }

      if (!linkedinShare && (!approvedForSocialMessage?.message?.length > 0)
        && !approvedForSocialMessage) {
        setShareMessage(story.body ? `${JSON.parse(story.body).blocks.map(block => block.text).join("\n")}` : "");
      }

      if (approvedForSocialMessage) {
        setShareMessage(approvedForSocialMessage.message);
        if (approvedForSocialMessage.scheduledAt) {
          setSchedulePost(true);
          setScheduleAt(new Date(approvedForSocialMessage.scheduledAt));
        }
      }
    }, [approvedForSocial, story, profile, linkedinShare, approvedForSocialMessage]
  );

  const authenticateUser = useCallback(
    async () => {
      let locationUrl;
      if (location.pathname.startsWith("/stories/trend")) {
        locationUrl = `${location.pathname}/story/${story.id}/share`;
      } else {
        locationUrl = `/stories/${story.id}/share`;
      }
      const { url } = await makeGet("/linkedin/integration", { url: locationUrl });
      window.location.href = url;
    },
    [location.pathname, story.id]
  );

  const onOpen = useCallback(
    async () => {
      if (!placeholder) {
        if (!profile?.userLinkedin?.linkedinId) {
          await authenticateUser();
          return;
        }
        setOpen(true);
      }
    },
    [authenticateUser, setOpen, profile, placeholder]
  );

  const onClose = useCallback(
    () => {
      setOpen(false);
    },
    [setOpen]
  );

  const handleCreateLinkedinPost = async () => {
    const editorBody = await editorRef.current.serialize();
    const body = {
      storyId: story.id,
      linkedinAuthor,
      visibility,
      message: formatLinkedinMention(JSON.parse(editorBody)),
      scheduledAt: schedulePost ? scheduleAt : undefined
    };

    if (orgsInfo[linkedinAuthor]) {
      body.orgName = orgsInfo[linkedinAuthor].localizedName;
      body.orgLogo = orgsInfo[linkedinAuthor].logoV2["original~"].elements[0].identifiers[0].identifier;
    }

    try {
      if (approvedForSocialMessage) {
        const { approvedForSocialMessage: updated } = await makePatch(`/stories/approved/message/${approvedForSocialMessage.id}`, body);
        if (onEdit) onEdit(updated);
      } else if (linkedinShare) {
        await makePost(`/linkedin/posts/${linkedinShare.id}/shares`, body);
      } else {
        const { approvedForSocial: created } = await makePost("/linkedin/posts", body);
        if (onCreate) {
          onCreate(created);
        }
      }
    } catch (error) {
      if (error.status === 403) {
        await authenticateUser();
      } else {
        setError(error);
      }
    }

    onClose();
    if (onShared) {
      onShared();
    }
  };

  const handlePlay = () => {
    mediaRef.current.play();
  };

  const onAuthorChanged = value => {
    if (value !== profile?.userLinkedin?.linkedinId) {
      setVisibility("PUBLIC");
    }

    setLinkedinAuthor(value);
  };

  const onVisibilityChanged = value => {
    setVisibility(value);
  };

  const handleRadioClick = value => {
    setSchedulePost(value);
  };

  const handleScheduleAtChange = newScheduleAt => {
    setScheduleAt(newScheduleAt);
  };

  const scheduleValidator = value => {
    const date = new Date(value);
    const now = new Date();
    if (date < now) {
      return t("You can not schedule a post for a past time");
    }
    return null;
  };

  const imageUrl = story.media.mediaType === "image" ? getImageFromStory(imagesSizes.NORMAL, story) : story.media.thumbnail;

  if (linkedinShare) {
    return (
      <>
        <PlainButton onClick={onOpen}>
          <Icon icon="linkedin-share" />
          <Trans>Share</Trans>
        </PlainButton>
        {open && (
          <ChqModalDialog onClose={onClose} className="LinkedinShareModal">
            <ModalDialog.Heading onClose={onClose}>
              <img className="social-icon" src={linkedin} alt="LinkedIn icon" />{" "}
              <Trans>Share on LinkedIn</Trans>
            </ModalDialog.Heading>
            <ModalDialog.Body>
              {error && <ErrorMessage error={error} />}
              <div className="Body_Header">
                <ProfileLink user={profile} aria-label={getUserName(profile)}>
                  <Thumbnail className="Thumbnail" image={profile.avatar.thumbUrl} title={getUserName(profile)} />
                </ProfileLink>
                <SelectField
                  imageIconPath="/linkedin/user.svg"
                  value={linkedinAuthor}
                  onChange={onAuthorChanged}
                  options={linkedinOrganizations}
                  required
                />
                <SelectField
                  imageIconPath="/linkedin/world.svg"
                  value={visibility}
                  onChange={onVisibilityChanged}
                  options={linkedinVisibilityOptions}
                  disabled={linkedinAuthor !== profile?.userLinkedin?.linkedinId}
                  required
                />
              </div>
              <LazyEditor
                initialValue={shareMessage}
                sections={["mention"]}
                ref={editorRef}
                placeholder={t("Add a message here...")}
                maxLength={general.LINKEDIN_CHARS_RESTRICTION}
                onExceededLength={setError}
                suggestionStrategy="linkedin"
              />
              <div className="Linkedin-Share__Container">
                <div className="Linkedin-Share__Container-Header">
                  <LinkedinPostCreator linkedinShare={linkedinShare} />
                  <p className="share-message">
                    <LinkedinMentionText value={linkedinShare.message} />
                  </p>
                </div>
                <div className="gallery-lightbox__photo">
                  <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${imageUrl})` }} />
                  {story.media.mediaType === "image" && (
                    <img
                      className="gallery-lightbox__photo--image"
                      ref={mediaRef}
                      src={imageUrl}
                      alt={story.imageAlt || story.question.question}
                    />
                  )}
                  {story.media.mediaType === "video" && (
                    <>
                      {showVideoInfo && (
                        <div className="gallery-lightbox__video-play" onClick={handlePlay}><Icon icon="play-filled" /></div>
                      )}
                      <video
                        controls
                        className="gallery-lightbox__main-image gallery-lightbox__main-video"
                        onPause={() => setShowVideoInfo(true)}
                        onPlay={() => setShowVideoInfo(false)}
                        poster={story.media.thumbnail}
                        ref={mediaRef}
                        src={story.media.url}
                        disablePictureInPicture
                        controlsList="nodownload"
                      />
                    </>
                  )}
                </div>
              </div>
            </ModalDialog.Body>
            <ModalDialog.Footer>
              <div>
                <ActionButton primary onClick={handleCreateLinkedinPost} disabled={!!error}>
                  <Trans>Share</Trans>
                </ActionButton>
              </div>
            </ModalDialog.Footer>
          </ChqModalDialog>
        )}
      </>
    );
  }

  return (
    <>
      <ButtonWrapper disabled={disabled} onOpen={onOpen}>
        {disabled ? (
          <Tooltip tip={t(`LinkedIn does not allow to share this image resolution, you have to share an image bigger than ${WIDTH_THRESHOLD}px X ${HEIGHT_THRESHOLD}px`)}>
            {children}
          </Tooltip>
        ) : children}
      </ButtonWrapper>
      {open && (
        <ChqModalDialog onClose={onClose} className="LinkedinShareModal">
          <ModalDialog.Heading onClose={onClose}>
            <img className="social-icon" src={linkedin} alt="LinkedIn icon" />{" "}
            {approvedForSocialMessage ? (
              <Trans>Edit your scheduled post</Trans>
            ) : (
              <Trans>Share this story on LinkedIn</Trans>
            )}
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {error && <ErrorMessage error={error} />}
            {!approvedForSocialMessage && (
              <div className="Body_Header">
                <ProfileLink user={profile} aria-label={getUserName(profile)}>
                  <Thumbnail className="Thumbnail" image={profile.avatar.thumbUrl} title={getUserName(profile)} />
                </ProfileLink>
                <div className="options-creator">
                  <SelectField
                    imageIconPath="/linkedin/user.svg"
                    value={linkedinAuthor}
                    onChange={onAuthorChanged}
                    options={linkedinOrganizations}
                    required
                  />
                  <SelectField
                    imageIconPath="/linkedin/world.svg"
                    value={visibility}
                    onChange={onVisibilityChanged}
                    options={linkedinVisibilityOptions}
                    disabled={linkedinAuthor !== profile?.userLinkedin?.linkedinId}
                    required
                  />
                </div>
              </div>
            )}
            <LazyEditor
              initialValue={shareMessage}
              sections={["mention"]}
              ref={editorRef}
              placeholder={t("Add a message here...")}
              maxLength={general.LINKEDIN_CHARS_RESTRICTION}
              onExceededLength={setError}
              suggestionStrategy="linkedin"
            />
            <div className="gallery-lightbox__photo">
              <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${imageUrl})` }} />
              {story.media.mediaType === "image" && (
                <>
                  <LinkedinImageSlider
                    linkedinImages={story.visuals}
                    mediaRef={mediaRef}
                    story={story}
                  />
                </>
              )}
              {story.media.mediaType === "video" && (
                <>
                  {showVideoInfo && (
                    <div className="gallery-lightbox__video-play" onClick={handlePlay}><Icon icon="play-filled" /></div>
                  )}
                  <video
                    controls
                    className="gallery-lightbox__main-image gallery-lightbox__main-video"
                    onPause={() => setShowVideoInfo(true)}
                    onPlay={() => setShowVideoInfo(false)}
                    poster={`${story.media.thumbnail}?not-from-cache-please`}
                    ref={mediaRef}
                    src={story.media.url}
                    disablePictureInPicture
                    crossOrigin="anonymous"
                    controlsList="nodownload"
                  >
                    <Subtitles media={story.media} />
                  </video>
                </>
              )}
            </div>
          </ModalDialog.Body>
          <ModalDialog.Footer>
            <div className="schedule-later">
              <BooleanField
                onChange={handleRadioClick}
                value={schedulePost}
              />
              <Trans>Schedule for</Trans>
            </div>
            <div className="schedule-at-later">
              <Icon icon="ios-calendar-outline" className={classnames("calendar-icon", { disabled: !schedulePost })} />
              <DateTimeField
                ariaLabel={t("Schedule the post for later")}
                disabled={!schedulePost}
                name="scheduleAt"
                offset={timezoneOffset}
                value={scheduleAt}
                onChange={handleScheduleAtChange}
                required
                validator={scheduleValidator}
              />
            </div>
            <div>
              <ActionButton onClick={handleCreateLinkedinPost} disabled={!!error}>
                {schedulePost ? <Trans>Save and Schedule</Trans> : <Trans>Post</Trans>}
              </ActionButton>
            </div>
          </ModalDialog.Footer>
        </ChqModalDialog>
      )}
    </>
  );
};

export default StoryShareLinkedin;
